import { Sparkles, Check, X, Clock3, Target } from 'lucide-react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { formatTime } from '../../utils/locale';

/**
 * Plan-day preview diff.
 * Replaces the old fire-and-forget toast with an inline accept/discard UI so
 * the operator can review the AI's proposed schedule before it lands on the
 * calendar.
 *
 * Props:
 *  - open: boolean
 *  - blocks: Array of proposed time blocks
 *  - usedFallback: boolean (true if AI fell back to a heuristic plan)
 *  - onAccept(): apply
 *  - onDiscard(): cancel
 */
export default function PlanDayPreviewModal({ open, blocks, usedFallback, onAccept, onDiscard }) {
  useBodyScrollLock(open);
  if (!open) return null;
  const total = blocks?.length || 0;
  const totalMinutes = (blocks || []).reduce((sum, b) => {
    const [sh, sm] = (b.startTime || '00:00').split(':').map(Number);
    const [eh, em] = (b.endTime || '00:00').split(':').map(Number);
    return sum + Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
  }, 0);

  return (
    <div
      data-testid="plan-day-preview-modal"
      className="fixed inset-0 z-[60] overflow-y-auto"
      style={{ background: 'var(--pd-overlay)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="flex min-h-full items-center justify-center px-4 py-8"
        onClick={(e) => { if (e.target === e.currentTarget) onDiscard?.(); }}
      >
      <div
        className="w-full max-w-[560px] rounded-[28px] overflow-hidden flex flex-col animate-fade-in"
        style={{ background: 'var(--pd-popover)', border: '1px solid var(--pd-popover-border)', boxShadow: 'var(--pd-shadow-3)', maxHeight: 'calc(100vh - 4rem)' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-3" style={{ borderBottom: '1px solid var(--pd-border)' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] font-semibold" style={{ color: usedFallback ? 'var(--pd-warning)' : 'var(--pd-focus)' }}>
                <Sparkles className="w-3 h-3" />
                {usedFallback ? 'Heuristic plan' : 'AI proposed plan'} · review
              </div>
              <h2 className="text-[20px] font-medium mt-2" style={{ letterSpacing: '-0.04em', color: 'var(--pd-text)' }}>
                {total} new block{total === 1 ? '' : 's'} ready to land
              </h2>
              <p className="text-[11px] mt-1" style={{ color: 'var(--pd-subtle)' }}>
                {totalMinutes > 0 ? `${Math.round(totalMinutes / 60 * 10) / 10}h of focused time scheduled.` : 'No new blocks proposed.'}
              </p>
            </div>
            <button data-testid="plan-day-preview-discard" onClick={onDiscard} className="pd-icon-button" aria-label="Discard plan">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Block list */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-3">
          {total === 0 ? (
            <div className="text-center py-10 text-[12px]" style={{ color: 'var(--pd-subtle)' }}>
              Your calendar is already optimal — nothing to add.
            </div>
          ) : (
            <ol className="space-y-2">
              {blocks.map((b, i) => (
                <li
                  key={b.id || `proposed-${i}`}
                  data-testid={`plan-day-preview-row-${i}`}
                  className="flex items-center gap-3 p-3 rounded-2xl"
                  style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}
                >
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'var(--pd-focus)18', color: 'var(--pd-focus)' }}>
                    <Target className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold truncate" style={{ color: 'var(--pd-text)' }}>{b.title || 'Focused work'}</div>
                    <div className="text-[10px] flex items-center gap-1.5 mt-1" style={{ color: 'var(--pd-subtle)' }}>
                      <Clock3 className="w-2.5 h-2.5" />
                      {formatTime(b.startTime)} – {formatTime(b.endTime)}
                      {b.blockType && b.blockType !== 'TASK' ? <span className="ml-1 px-1.5 py-[1px] rounded uppercase tracking-[0.06em] font-semibold" style={{ background: 'var(--pd-panel)' }}>{b.blockType}</span> : null}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderTop: '1px solid var(--pd-border)' }}>
          <button
            data-testid="plan-day-preview-revert"
            onClick={onDiscard}
            className="text-[12px] font-medium px-4 py-2 rounded-full"
            style={{ color: 'var(--pd-muted)' }}
          >
            Revert
          </button>
          <button
            data-testid="plan-day-preview-accept"
            onClick={onAccept}
            disabled={total === 0}
            className="px-5 py-2.5 rounded-full text-[12px] font-semibold flex items-center gap-2 transition-opacity"
            style={{ background: 'var(--pd-green)', color: 'white', opacity: total === 0 ? 0.4 : 1 }}
          >
            Accept plan <Check className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}
